import { useTheme } from '@mui/material';
import { chartSeriesOrder } from '@shared/styles';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface CompetitorSeries {
  id: number;
  name: string;
  colorIndex: number;
}

interface Props {
  buckets: string[];
  bucketLabels: string[];
  series: CompetitorSeries[];
  rows: Record<string, number | string>[];
  height?: number;
}

export const CompetitorPricesChart = ({
  buckets,
  bucketLabels,
  series,
  rows,
  height = 240,
}: Props) => {
  const theme = useTheme();
  const { palette } = theme.vars;

  const colorFor = (colorIndex: number) =>
    palette.chart.series[chartSeriesOrder[colorIndex % chartSeriesOrder.length]];

  const axisColor = palette.text.secondary;
  const gridColor = palette.divider;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="bucket"
          type="category"
          tickFormatter={(value: string) => bucketLabels[buckets.indexOf(value)] ?? value}
          tick={{ fill: axisColor, fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: gridColor }}
          minTickGap={16}
        />
        <YAxis
          allowDecimals={false}
          width={48}
          tick={{ fill: axisColor, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          contentStyle={{
            background: palette.background.paper,
            border: `1px solid ${palette.divider}`,
            borderRadius: 8,
            color: palette.text.primary,
            fontSize: 12,
          }}
          labelFormatter={(value) => bucketLabels[buckets.indexOf(String(value))] ?? String(value)}
        />
        <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} />
        {series.map((s) => (
          <Line
            key={s.id}
            type="monotone"
            dataKey={String(s.id)}
            name={s.name}
            stroke={colorFor(s.colorIndex)}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
